import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import posthog from 'posthog-js'
import { apiClient } from '@/api/http/client'
import { queryKeys } from '@/api/query-keys'
import type { ApiResponse } from '@/api/http/types'
import {
  applyLoggedUserToSession,
  ensureSpSessionHydrated,
  fetchAndApplySession,
  type AuthLoggedUserPayload,
} from '@/features/auth/api/useAuth'
import {
  IdentityAuthPath,
  persistWebSurveyOnboardingSession,
  persistWebSurveySurveyFirst,
} from '@/features/auth/lib/constants'
import {
  captureAccountCreated,
  captureMobileVerified,
  captureSignupSurveyCompleted,
} from '@/lib/analytics'
import { runDetached } from '@/shared/lib/runDetachedPromise'
import type {
  SpConsentStatusData,
  SpInstrumentLinkStartData,
  SpInstrumentOtpSendData,
  SpInstrumentOtpVerifyData,
  SpOnboardingChannel,
} from '../lib/types'

const publicConfig = { skipUnauthorizedRedirect: true } as const

export function useInstrumentLinkStart() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await apiClient.post<
        ApiResponse<SpInstrumentLinkStartData>
      >(IdentityAuthPath.SP_INSTRUMENT_LINK_START, { token }, publicConfig)
      return data.data
    },
    onSuccess: async (data) => {
      if (!data?.flow || data.flow === 'onboarding') {
        return
      }
      try {
        await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: fetchAndApplySession,
        })
      } catch {
        await ensureSpSessionHydrated()
      }
    },
  })
}

export function useInstrumentOtpSend() {
  return useMutation({
    mutationFn: async (body: {
      onboardingSessionId: string
      channel: SpOnboardingChannel
      phone?: string
    }) => {
      const { data } = await apiClient.post<
        ApiResponse<SpInstrumentOtpSendData>
      >(IdentityAuthPath.SP_INSTRUMENT_LINK_OTP_SEND, body, publicConfig)
      return data.data
    },
  })
}

export function useInstrumentOtpResend() {
  return useMutation({
    mutationFn: async (onboardingSessionId: string) => {
      const { data } = await apiClient.post<
        ApiResponse<SpInstrumentOtpSendData>
      >(
        IdentityAuthPath.SP_INSTRUMENT_LINK_OTP_RESEND,
        { onboardingSessionId },
        publicConfig
      )
      return data.data
    },
  })
}

export function useInstrumentOtpVerify() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: { onboardingSessionId: string; code: string }) => {
      const { data } = await apiClient.post<
        ApiResponse<SpInstrumentOtpVerifyData>
      >(
        IdentityAuthPath.SP_INSTRUMENT_LINK_OTP_VERIFY,
        {
          onboardingSessionId: body.onboardingSessionId,
          code: body.code,
          platform: 'web',
        },
        publicConfig
      )
      return data.data
    },
    onSuccess: async (data) => {
      posthog.capture('instrument_link_otp_verified')
      captureMobileVerified({ surveyId: data?.instrumentId, campaignId: data?.campaignId })
      if (data?.loggedUser) {
        applyLoggedUserToSession(data.loggedUser as AuthLoggedUserPayload)
      }
      try {
        await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: fetchAndApplySession,
        })
      } catch {
        // Cookies + loggedUser from verify are enough to continue consent.
      }
    },
  })
}

export function useCreateWebSurveyAccount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (body: {
      onboardingSessionId: string
      firstName: string
      lastName: string
      email: string
      password: string
      confirmPassword: string
      registeredPoliticalParty?: string
      acceptedTerms?: boolean
      acceptedPrivacy?: boolean
    }) => {
      const { registeredPoliticalParty, ...rest } = body
      const payload = {
        ...rest,
        platform: 'web',
        ...(registeredPoliticalParty ? { registeredPoliticalParty } : {}),
        ...(body.acceptedTerms ? { acceptedTerms: true } : {}),
        ...(body.acceptedPrivacy ? { acceptedPrivacy: true } : {}),
      }
      const { data } = await apiClient.post<
        ApiResponse<SpInstrumentOtpVerifyData>
      >(
        IdentityAuthPath.SP_INSTRUMENT_LINK_ACCOUNT,
        payload,
        publicConfig,
      )
      return data.data
    },
    onSuccess: async (data) => {
      const funnelCtx = { surveyId: data?.instrumentId, campaignId: data?.campaignId }
      captureAccountCreated(funnelCtx)
      if (data?.loggedUser) {
        applyLoggedUserToSession(data.loggedUser as AuthLoggedUserPayload)
      }
      try {
        await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: fetchAndApplySession,
        })
      } catch {
        await ensureSpSessionHydrated()
      }
      captureSignupSurveyCompleted({
        ...funnelCtx,
        surveyLinked: data?.surveyLinked,
        source: 'account_created',
      })
    },
  })
}

export function useStartSurveyFirstSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (token: string) => {
      const { data } = await apiClient.post<
        ApiResponse<{
          pendingSessionId?: string
          onboardingSessionId?: string
          guestSessionId?: string
          instrumentId?: string
          campaignId?: string
          guest?: boolean
        }>
      >(IdentityAuthPath.SP_WEB_SURVEY_ANONYMOUS_SESSION, { token }, publicConfig)
      return data.data
    },
    onSuccess: async (data) => {
      const sessionId =
        data?.pendingSessionId || data?.onboardingSessionId || data?.guestSessionId
      if (sessionId) {
        persistWebSurveyOnboardingSession(sessionId)
      }
      persistWebSurveySurveyFirst(data?.instrumentId)
      try {
        await queryClient.fetchQuery({
          queryKey: queryKeys.auth.me(),
          queryFn: fetchAndApplySession,
        })
      } catch {
        await ensureSpSessionHydrated()
      }
    },
  })
}

export function useCheckWebSurveyPhone() {
  return useMutation({
    mutationFn: async (body: { onboardingSessionId: string; phone: string }) => {
      const { data } = await apiClient.post<ApiResponse<{ available: boolean }>>(
        IdentityAuthPath.SP_WEB_SURVEY_PHONE_CHECK,
        body,
        publicConfig,
      )
      return data.data
    },
  })
}

export function useCheckWebSurveyEmail() {
  return useMutation({
    mutationFn: async (email: string) => {
      const { data } = await apiClient.post<ApiResponse<{ available: boolean }>>(
        IdentityAuthPath.SP_WEB_SURVEY_EMAIL_CHECK,
        { email },
        publicConfig,
      )
      return data.data
    },
  })
}

export function useClaimPendingSurvey() {
  return useMutation({
    mutationFn: async (pendingSessionId: string) => {
      const { data } = await apiClient.post<
        ApiResponse<{ claimed: boolean; surveyLinked?: boolean }>
      >(
        IdentityAuthPath.SP_WEB_SURVEY_CLAIM_PENDING,
        { pendingSessionId },
      )
      return data.data
    },
    onSuccess: (data) => {
      captureSignupSurveyCompleted({
        surveyLinked: data?.surveyLinked,
        source: 'claim_pending',
      })
    },
  })
}

export function useSpConsentStatus(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.spConsent.status(),
    queryFn: async () => {
      const { data } = await apiClient.get<ApiResponse<SpConsentStatusData>>(
        IdentityAuthPath.SP_CONSENT_STATUS
      )
      return data.data
    },
    enabled,
    retry: false,
  })
}

export function useAcceptSpPrivacy() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (policyVersion?: string) => {
      const { data } = await apiClient.post<ApiResponse<SpConsentStatusData>>(
        IdentityAuthPath.SP_CONSENT_PRIVACY_ACCEPT,
        policyVersion ? { policyVersion } : {}
      )
      return data.data
    },
    onSuccess: () => {
      runDetached(
        queryClient.invalidateQueries({
          queryKey: queryKeys.spConsent.status(),
        }),
      )
    },
  })
}

export function useAcceptSpTerms() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (policyVersion?: string) => {
      const { data } = await apiClient.post<ApiResponse<SpConsentStatusData>>(
        IdentityAuthPath.SP_CONSENT_TERMS_ACCEPT,
        policyVersion ? { policyVersion } : {}
      )
      return data.data
    },
    onSuccess: () => {
      runDetached(
        queryClient.invalidateQueries({
          queryKey: queryKeys.spConsent.status(),
        }),
      )
    },
  })
}
